import { ReactElement } from 'react';

export type AxisRange = {
  /** Raw input text; empty means "automatic". */
  min: string;
  max: string;
};

export const EMPTY_AXIS_RANGE: AxisRange = { min: '', max: '' };

/**
 * Parse one end of an axis range typed by the user.
 * @param text - Input text.
 * @returns A finite number, or null when blank or not a number.
 */
export function parseAxisValue(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/**
 * Resolve the user's min/max overrides against the automatic bounds. Either end
 * may be left blank. A pair that is not ascending, or a non-positive bound on a
 * log axis, is ignored and reported as invalid so the control can say so.
 * @param range - Typed overrides.
 * @param autoMin - Automatic lower bound.
 * @param autoMax - Automatic upper bound.
 * @param log - Whether the axis is logarithmic (bounds must be positive).
 * @returns Effective bounds and whether the typed pair was usable.
 */
export function resolveAxisRange(
  range: AxisRange,
  autoMin: number,
  autoMax: number,
  log: boolean
): { min: number; max: number; overridden: boolean; invalid: boolean } {
  const typedMin = parseAxisValue(range.min);
  const typedMax = parseAxisValue(range.max);
  const min = typedMin ?? autoMin;
  const max = typedMax ?? autoMax;
  const overridden = typedMin !== null || typedMax !== null;
  const invalid = overridden && (!(min < max) || (log && min <= 0));
  if (invalid) {
    return { min: autoMin, max: autoMax, overridden: false, invalid: true };
  }
  return { min, max, overridden, invalid: false };
}

type AxisRangeControlProps = {
  /** Axis name shown before the inputs, e.g. "Y axis". */
  label: string;
  /** Unit shown after the inputs. */
  unit: string;
  range: AxisRange;
  /** Automatic bounds, shown as placeholders so the user knows what "auto" is. */
  autoMin: string;
  autoMax: string;
  invalid: boolean;
  onChange: (next: AxisRange) => void;
};

/**
 * Min / max inputs for one chart axis. Blank means automatic; a Reset button
 * appears once anything has been typed.
 * @param props - Current range and change callback.
 * @returns Inline control row.
 */
export default function AxisRangeControl(props: AxisRangeControlProps): ReactElement {
  const { label, unit, range, autoMin, autoMax, invalid, onChange } = props;
  const inputClass = `w-24 rounded border bg-background-700 px-2 py-1 text-base text-gray-100 focus:outline-none ${
    invalid ? 'border-error-500' : 'border-gray-800 focus:border-primary-500'
  }`;
  const touched = range.min !== '' || range.max !== '';
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-base text-gray-400">
      <span>{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step="any"
        aria-label={`${label} minimum`}
        placeholder={autoMin}
        value={range.min}
        onChange={(e) => onChange({ ...range, min: e.target.value })}
        className={inputClass}
      />
      <span>–</span>
      <input
        type="number"
        inputMode="decimal"
        step="any"
        aria-label={`${label} maximum`}
        placeholder={autoMax}
        value={range.max}
        onChange={(e) => onChange({ ...range, max: e.target.value })}
        className={inputClass}
      />
      <span>{unit}</span>
      {invalid && <span className="text-error-500">min must be below max</span>}
      {touched && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_AXIS_RANGE)}
          className="rounded border border-gray-800 bg-background-700 px-2 py-1 text-base text-gray-400 transition hover:text-gray-200"
        >
          Auto
        </button>
      )}
    </div>
  );
}
